import { useState, useEffect, useRef } from "react";
import {
  Plus, Pencil, ExternalLink, Building2, Search,
  CheckCircle2, XCircle, LayoutGrid, ChevronLeft, ChevronRight,
  Loader2, Trash2, Users, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import PlacementService from "./Placement.service";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------
const employmentTypes = [ "Full Time", "Internship", "Contract" ];
const workModes = [ "Onsite", "Remote", "Hybrid" ];
const availableGenres = [
  "Technology", "Data Science", "Product Management", "Design", "Business",
  "Consulting", "Finance", "Marketing", "Sales", "Operations",
];

// ---------------------------------------------------------------------------
// TYPES
// ---------------------------------------------------------------------------
type Job = {
  id: string;
  company: string;
  title: string;
  description: string;
  employmentType: string;
  workMode: string;
  genre: string;
  siteUrl: string;
  isActive: boolean;
};

type Applicant = {
  id: string;
  user_uid: string;
  e_id: string;
  applied_at: string;
  name: string;
  email: string;
};

type FormErrors = Partial<Record<keyof Job, string>>;

const emptyForm: Job = {
  id: "", company: "", title: "", description: "",
  employmentType: "", workMode: "", genre: "", siteUrl: "", isActive: true,
};

// ---------------------------------------------------------------------------
// SKELETON ROWS
// ---------------------------------------------------------------------------
const pulse = "animate-pulse rounded bg-muted";

function JobSkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <TableRow key={i} className="border-border/40">
          <TableCell className="pl-6 py-4">
            <div className="flex items-center gap-2">
              <div className={`h-8 w-8 ${pulse}`} />
              <div className={`h-3.5 w-28 ${pulse}`} />
            </div>
          </TableCell>
          <TableCell><div className={`h-3.5 w-36 ${pulse}`} /></TableCell>
          <TableCell><div className={`h-5 w-20 ${pulse}`} /></TableCell>
          <TableCell className="text-center"><div className={`h-5 w-14 mx-auto ${pulse}`} /></TableCell>
          <TableCell className="pr-6">
            <div className="flex justify-end gap-1">
              <div className={`h-8 w-8 ${pulse}`} />
              <div className={`h-8 w-8 ${pulse}`} />
              <div className={`h-8 w-8 ${pulse}`} />
            </div>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

function ApplicantSkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <TableRow key={i} className="border-border/40">
          <TableCell className="pl-6 py-3"><div className={`h-3.5 w-32 ${pulse}`} /></TableCell>
          <TableCell><div className={`h-3.5 w-44 ${pulse}`} /></TableCell>
          <TableCell><div className={`h-3.5 w-24 ${pulse}`} /></TableCell>
          <TableCell className="pr-6"><div className={`h-3.5 w-24 ${pulse}`} /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// PAGE
// ---------------------------------------------------------------------------
export default function PlacementsPage() {

  // ── JOBS STATE ─────────────────────────────────────────────────────────
  const [ jobs, setJobs ] = useState<Job[]>([]);
  const [ jobsLoading, setJobsLoading ] = useState(false);
  const [ search, setSearch ] = useState("");
  const [ currentPage, setCurrentPage ] = useState(1);
  const [ itemsPerPage, setItemsPerPage ] = useState(5);
  const [ totalItems, setTotalItems ] = useState(0);
  const [ totalActiveItems, setTotalActiveItems ] = useState(0);
  const [ totalInactiveItems, setTotalInactiveItems ] = useState(0);

  // ── JOB FORM STATE ──────────────────────────────────────────────────────
  const [ formOpen, setFormOpen ] = useState(false);
  const [ editingIndex, setEditingIndex ] = useState<number | null>(null);
  const [ form, setForm ] = useState<Job>(emptyForm);
  const [ errors, setErrors ] = useState<FormErrors>({});
  const [ formSaving, setFormSaving ] = useState(false);

  // ── APPLICANTS DRAWER STATE ─────────────────────────────────────────────
  const [ applicantsOpen, setApplicantsOpen ] = useState(false);
  const [ selectedJob, setSelectedJob ] = useState<Job | null>(null);
  const [ applicants, setApplicants ] = useState<Applicant[]>([]);
  const [ applicantsLoading, setApplicantsLoading ] = useState(false);
  const [ applicantsPage, setApplicantsPage ] = useState(1);
  const [ applicantsTotal, setApplicantsTotal ] = useState(0);
  const [ applicantsPerPage ] = useState(8);
  const [ exportLoading, setExportLoading ] = useState(false);

  // ── DELETE STATE ────────────────────────────────────────────────────────
  const [ deleteDialogOpen, setDeleteDialogOpen ] = useState(false);
  const [ pendingDeleteJob, setPendingDeleteJob ] = useState<Job | null>(null);
  const [ deleteLoading, setDeleteLoading ] = useState(false);

  const placementService = useRef(new PlacementService());

  // ── FETCH JOBS ───────────────────────────────────────────────────────────
  const fetchJobs = async () => {
    try {
      setJobsLoading(true);
      const result = await placementService.current.fetchJobs({
        page: currentPage,
        size: itemsPerPage,
        search,
        totalCount: currentPage === 1,
      });
      if (!result.success) { console.error("Failed fetching jobs"); return; }
      if (result.counts) {
        setTotalItems(result.counts.total);
        setTotalActiveItems(result.counts.active);
        setTotalInactiveItems(result.counts.inactive);
      }
      setJobs(result.jobs);
    } catch (err) {
      console.error("Fetch jobs error:", err);
    } finally {
      setJobsLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); }, [ currentPage, search, itemsPerPage ]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // ── FETCH APPLICANTS ─────────────────────────────────────────────────────
  const fetchApplicants = async (jobId: string, page: number) => {
    try {
      setApplicantsLoading(true);
      const result = await placementService.current.fetchApplicants({
        jobId,
        page,
        size: applicantsPerPage,
      } as any);
      if (!result.success) { console.error("Failed fetching applicants"); return; }
      setApplicants(result.applicants as Applicant[]);
      setApplicantsTotal(result.total);
    } catch (err) {
      console.error("Fetch applicants error:", err);
    } finally {
      setApplicantsLoading(false);
    }
  };

  const handleOpenApplicants = (job: Job) => {
    setSelectedJob(job);
    setApplicants([]);
    setApplicantsPage(1);
    setApplicantsTotal(0);
    setApplicantsOpen(true);
    fetchApplicants(job.id, 1);
  };

  const handleApplicantsPageChange = (page: number) => {
    setApplicantsPage(page);
    if (selectedJob) fetchApplicants(selectedJob.id, page);
  };

  const applicantsTotalPages = Math.ceil(applicantsTotal / applicantsPerPage);

  // ── CSV EXPORT ───────────────────────────────────────────────────────────
  const handleExportCSV = async () => {
    if (!selectedJob) return;
    setExportLoading(true);
    try {
      await (placementService.current as any).exportApplicantsCSV(selectedJob.id);
    } catch (err) {
      console.error("CSV export error:", err);
    } finally {
      setExportLoading(false);
    }
  };

  // ── JOB FORM HANDLERS ────────────────────────────────────────────────────
  const handleChange = (field: keyof Job, value: string | boolean) => {
    setForm(prev => ({ ...prev, [ field ]: value }));
    if (errors[ field ]) setErrors(prev => ({ ...prev, [ field ]: undefined }));
  };

  const toJobPayload = (job: Job) => ({
    title: job.title, company: job.company, genre: job.genre,
    employmentType: job.employmentType, workMode: job.workMode,
    description: job.description || "", siteUrl: job.siteUrl, isActive: job.isActive ?? true,
  });

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.company.trim()) newErrors.company = "Company is required";
    if (!form.title.trim()) newErrors.title = "Job title is required";
    if (!form.siteUrl.trim()) newErrors.siteUrl = "URL is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setFormSaving(true);
      const payload = toJobPayload(form);
      const result = editingIndex !== null
        ? await placementService.current.updateJob(jobs[ editingIndex ].id, payload)
        : await placementService.current.createJob(payload);
      if (!result.success) { console.error("Job save failed"); return; }
      setFormOpen(false);
      await fetchJobs();
    } catch (err) {
      console.error("Save error:", err);
    } finally {
      setFormSaving(false);
    }
  };

  // ── DELETE HANDLERS ──────────────────────────────────────────────────────
  const handleDeleteClick = (job: Job) => {
    setPendingDeleteJob(job);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteJob) return;
    try {
      setDeleteLoading(true);
      const result = await placementService.current.deleteJob(pendingDeleteJob.id);
      if (!result.success) { console.error("Delete failed"); return; }
      setDeleteDialogOpen(false);
      setPendingDeleteJob(null);
      if (jobs.length === 1 && currentPage > 1) setCurrentPage(p => p - 1);
      else await fetchJobs();
    } catch (err) {
      console.error("Delete error:", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── PAGINATION HELPERS ───────────────────────────────────────────────────
  const handlePageSizeChange = (val: string) => { setItemsPerPage(Number(val)); setCurrentPage(1); };
  const handleSearchChange = (val: string) => { setSearch(val); setCurrentPage(1); };

  // ── RENDER ───────────────────────────────────────────────────────────────
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[oklch(0.97_0.01_90)] p-6 md:p-8 text-foreground">

        {/* HEADER */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Placement Management</h1>
            <p className="mt-1 text-sm text-muted-foreground">Manage institutional job openings and intern tracking</p>
          </div>
          <Button
            onClick={() => { setForm(emptyForm); setEditingIndex(null); setErrors({}); setFormOpen(true); }}
            className="gap-2 shrink-0"
          >
            <Plus className="h-4 w-4" /> Add Job
          </Button>
        </div>

        {/* STATS */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            { icon: <LayoutGrid className="h-5 w-5" />, bg: "bg-primary/10 text-primary", label: "Total Listings", value: jobsLoading && totalItems === 0 ? "—" : totalItems },
            { icon: <CheckCircle2 className="h-5 w-5" />, bg: "bg-emerald-500/10 text-emerald-600", label: "Active Jobs", value: jobsLoading && totalActiveItems === 0 ? "—" : totalActiveItems },
            { icon: <XCircle className="h-5 w-5" />, bg: "bg-muted text-muted-foreground", label: "Inactive", value: jobsLoading && totalInactiveItems === 0 ? "—" : totalInactiveItems },
          ].map(stat => (
            <Card key={stat.label} className="border border-border/60 shadow-sm">
              <CardContent className="flex items-center gap-4 p-5">
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${stat.bg}`}>{stat.icon}</div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{stat.label}</p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* MAIN TABLE */}
        <Card className="border border-border/60 shadow-sm overflow-hidden bg-card">
          <CardHeader className="flex flex-col gap-4 pb-4 sm:flex-row sm:items-center sm:justify-between border-b border-border/40 bg-muted/5">
            <div>
              <CardTitle className="text-base font-semibold">Live Placements</CardTitle>
              <CardDescription className="text-sm">Database of current opportunities</CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={search}
                onChange={e => handleSearchChange(e.target.value)}
                className="pl-9 text-sm border-border/60"
              />
            </div>
          </CardHeader>

          <CardContent className="p-0">
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow className="border-border/60 hover:bg-transparent">
                  <TableHead className="pl-6 font-bold text-[11px] uppercase tracking-widest">Company</TableHead>
                  <TableHead className="font-bold text-[11px] uppercase tracking-widest">Job Title</TableHead>
                  <TableHead className="font-bold text-[11px] uppercase tracking-widest">Type</TableHead>
                  <TableHead className="font-bold text-[11px] uppercase tracking-widest text-center">Status</TableHead>
                  <TableHead className="pr-6 text-right font-bold text-[11px] uppercase tracking-widest">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobsLoading ? (
                  <JobSkeletonRows count={itemsPerPage} />
                ) : jobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-16 text-sm text-muted-foreground">
                      No job postings found
                    </TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job, i) => (
                    <TableRow key={job.id} className="border-border/40 hover:bg-muted/40 transition-colors group">
                      <TableCell className="pl-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded bg-primary/5 flex items-center justify-center border border-primary/10">
                            <Building2 className="h-4 w-4 text-primary" />
                          </div>
                          <span className="font-semibold text-sm">{job.company}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{job.title}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="font-medium text-[10px]">{job.employmentType}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={job.isActive ? "bg-emerald-500/10 text-emerald-600 border-0" : "bg-muted text-muted-foreground border-0"}>
                          {job.isActive ? "Active" : "Closed"}
                        </Badge>
                      </TableCell>
                      <TableCell className="pr-6 text-right">
                        <div className="flex justify-end gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => handleOpenApplicants(job)}>
                                <Users className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>View Applicants</TooltipContent>
                          </Tooltip>
                          <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" asChild>
                            <a href={job.siteUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /></a>
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setForm(job); setEditingIndex(i); setErrors({}); setFormOpen(true); }}>
                            <Pencil className="h-4 w-4 text-muted-foreground" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => handleDeleteClick(job)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* PAGINATION FOOTER */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between px-6 py-4 border-t border-border/60 bg-muted/5">
              <div className="flex items-center gap-6">
                <p className="text-[11px] text-muted-foreground whitespace-nowrap">
                  {jobsLoading ? (
                    <span className="text-muted-foreground/50">Loading...</span>
                  ) : (
                    <>
                      Showing{" "}
                      <span className="font-bold text-foreground">{totalItems === 0 ? 0 : Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}</span>
                      {" "}to{" "}
                      <span className="font-bold text-foreground">{Math.min(currentPage * itemsPerPage, totalItems)}</span>
                      {" "}of{" "}
                      <span className="font-bold text-foreground">{totalItems}</span>
                    </>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground/60 tracking-tighter">Rows:</span>
                  <Select value={itemsPerPage.toString()} onValueChange={handlePageSizeChange}>
                    <SelectTrigger className="h-7 w-[65px] text-[11px] bg-background border-border/60"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-8 border-border/60 text-[11px] font-bold" disabled={currentPage === 1 || jobsLoading} onClick={() => setCurrentPage(p => p - 1)}>
                  <ChevronLeft className="mr-1 h-3 w-3" /> Prev
                </Button>
                <div className="flex items-center px-3 h-8 rounded-md border border-border/60 bg-background text-[11px] font-bold">
                  {currentPage} / {totalPages || 1}
                </div>
                <Button variant="outline" size="sm" className="h-8 border-border/60 text-[11px] font-bold" disabled={currentPage === totalPages || totalPages === 0 || jobsLoading} onClick={() => setCurrentPage(p => p + 1)}>
                  Next <ChevronRight className="ml-1 h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>


        {/* ── JOB FORM DIALOG ── */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="max-w-2xl border-border/60">
            <DialogHeader className="border-b border-border/40 pb-4">
              <DialogTitle className="text-xl font-bold tracking-tight">
                {editingIndex !== null ? "Update Job Posting" : "Create New Opportunity"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">01. Identity</span>
                  <Separator className="flex-1 opacity-50" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Company Name</Label>
                    <Input value={form.company} onChange={e => handleChange("company", e.target.value)} placeholder="e.g., Tesla" />
                    {errors.company && <p className="text-[11px] text-red-500">{errors.company}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Job Title</Label>
                    <Input value={form.title} onChange={e => handleChange("title", e.target.value)} placeholder="e.g., Lead Architect" />
                    {errors.title && <p className="text-[11px] text-red-500">{errors.title}</p>}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">02. Setup</span>
                  <Separator className="flex-1 opacity-50" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Term</Label>
                    <Select value={form.employmentType} onValueChange={v => handleChange("employmentType", v)}>
                      <SelectTrigger className="text-xs border-black focus:border-black"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{employmentTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Mode</Label>
                    <Select value={form.workMode} onValueChange={v => handleChange("workMode", v)}>
                      <SelectTrigger className="text-xs border-black focus:border-black"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{workModes.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Vertical</Label>
                    <Select value={form.genre} onValueChange={v => handleChange("genre", v)}>
                      <SelectTrigger className="text-xs border-black focus:border-black"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{availableGenres.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">03. Details</span>
                  <Separator className="flex-1 opacity-50" />
                </div>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Application URL</Label>
                    <Input type="url" value={form.siteUrl} onChange={e => handleChange("siteUrl", e.target.value)} placeholder="https://company.com/careers/job" />
                    {errors.siteUrl && <p className="text-[11px] text-red-500">{errors.siteUrl}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-muted-foreground">Job Description</Label>
                    <Textarea rows={4} className="border-black focus-visible:border-black" value={form.description} onChange={e => handleChange("description", e.target.value)} placeholder="Describe responsibilities, requirements, and role overview..." />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 p-4 transition-all hover:bg-primary/10">
                  <div>
                    <p className="text-sm font-bold text-primary">Active Publication</p>
                    <p className="text-[11px] text-primary/70">Control whether students can view and apply to this listing</p>
                  </div>
                  <Switch checked={form.isActive} onCheckedChange={v => handleChange("isActive", v)} />
                </div>
              </div>

            </div>
            <DialogFooter className="bg-muted/30 -mx-6 -mb-6 p-6 border-t border-border/40">
              <Button variant="ghost" onClick={() => setFormOpen(false)} className="text-xs font-bold" disabled={formSaving}>Discard Changes</Button>
              <Button onClick={handleSave} disabled={formSaving} className="min-w-[140px] text-xs font-bold shadow-lg shadow-primary/20">
                {formSaving ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Saving...</> : editingIndex !== null ? "Update Record" : "Publish Opportunity"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* ── APPLICANTS DRAWER ── */}
        <Sheet open={applicantsOpen} onOpenChange={setApplicantsOpen}>
          <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col gap-0 p-0 bg-zinc-50">

            <SheetHeader className="px-6 py-5 border-b border-border/40">
              <div className="flex items-center justify-between">
                <div>
                  <SheetTitle className="text-lg font-bold">
                    {selectedJob?.company} — {selectedJob?.title}
                  </SheetTitle>
                  <SheetDescription className="text-xs mt-0.5">
                    {applicantsLoading && applicantsTotal === 0
                      ? "Loading applicants..."
                      : `${applicantsTotal} applicant${applicantsTotal !== 1 ? "s" : ""}`}
                  </SheetDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs font-bold"
                  onClick={handleExportCSV}
                  disabled={applicantsTotal === 0 || applicantsLoading || exportLoading}
                >
                  {exportLoading
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Download className="h-3.5 w-3.5" />}
                  {exportLoading ? "Exporting..." : "Export CSV"}
                </Button>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader className="bg-muted/30 sticky top-0 z-10">
                  <TableRow className="border-border/40 hover:bg-transparent">
                    <TableHead className="pl-6 font-bold text-[11px] uppercase tracking-widest">Name</TableHead>
                    <TableHead className="font-bold text-[11px] uppercase tracking-widest">Email</TableHead>
                    <TableHead className="font-bold text-[11px] uppercase tracking-widest">E_ID</TableHead>
                    <TableHead className="pr-6 font-bold text-[11px] uppercase tracking-widest">Applied</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applicantsLoading ? (
                    <ApplicantSkeletonRows count={applicantsPerPage} />
                  ) : applicants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-16 text-sm text-muted-foreground">
                        No applicants yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    applicants.map(a => (
                      <TableRow key={a.id} className="border-border/40 hover:bg-muted/40">
                        <TableCell className="pl-6 py-3 font-medium text-sm">{a.name ?? "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{a.email ?? "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{a.e_id ?? "—"}</TableCell>
                        <TableCell className="pr-6 text-sm text-muted-foreground">
                          {a.applied_at
                            ? new Date(a.applied_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* APPLICANTS PAGINATION */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-border/40 bg-muted/5 shrink-0">
              <p className="text-[11px] text-muted-foreground">
                {applicantsLoading ? (
                  <span className="text-muted-foreground/50">Loading...</span>
                ) : (
                  <>
                    Showing{" "}
                    <span className="font-bold text-foreground">
                      {applicantsTotal === 0 ? 0 : Math.min((applicantsPage - 1) * applicantsPerPage + 1, applicantsTotal)}
                    </span>
                    {" "}–{" "}
                    <span className="font-bold text-foreground">
                      {Math.min(applicantsPage * applicantsPerPage, applicantsTotal)}
                    </span>
                    {" "}of{" "}
                    <span className="font-bold text-foreground">{applicantsTotal}</span>
                  </>
                )}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-8 text-[11px] font-bold border-border/60" disabled={applicantsPage === 1 || applicantsLoading} onClick={() => handleApplicantsPageChange(applicantsPage - 1)}>
                  <ChevronLeft className="mr-1 h-3 w-3" /> Prev
                </Button>
                <div className="px-3 h-8 flex items-center rounded-md border border-border/60 bg-background text-[11px] font-bold">
                  {applicantsPage} / {applicantsTotalPages || 1}
                </div>
                <Button variant="outline" size="sm" className="h-8 text-[11px] font-bold border-border/60" disabled={applicantsPage >= applicantsTotalPages || applicantsTotalPages === 0 || applicantsLoading} onClick={() => handleApplicantsPageChange(applicantsPage + 1)}>
                  Next <ChevronRight className="ml-1 h-3 w-3" />
                </Button>
              </div>
            </div>

          </SheetContent>
        </Sheet>


        {/* ── DELETE DIALOG ── */}
        <Dialog open={deleteDialogOpen} onOpenChange={v => { if (!deleteLoading) { setDeleteDialogOpen(v); if (!v) setPendingDeleteJob(null); } }}>
          <DialogContent className="max-w-md border-border/60">
            <DialogHeader className="pb-2">
              <DialogTitle className="text-lg font-bold">Delete job posting?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground pb-2">
              This will permanently remove{" "}
              <span className="font-semibold text-foreground">
                {pendingDeleteJob ? `${pendingDeleteJob.title} at ${pendingDeleteJob.company}` : "this posting"}
              </span>. This action cannot be undone.
            </p>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="ghost" className="text-xs font-bold" disabled={deleteLoading} onClick={() => { setDeleteDialogOpen(false); setPendingDeleteJob(null); }}>Cancel</Button>
              <Button variant="destructive" className="text-xs font-bold min-w-[120px]" disabled={deleteLoading} onClick={confirmDelete}>
                {deleteLoading
                  ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Deleting...</>
                  : <><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete Posting</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </TooltipProvider>
  );
}